import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppStatus, AppStatusInfo } from '../../constants/appStatus';
import InternalMenu from '../../components/shared/InternalMenu';

// Helper function to format dates
const formatDate = (dateVal) => {
  if (!dateVal) return 'N/A';
  
  const date = typeof dateVal.toDate === 'function' 
    ? dateVal.toDate() 
    : new Date(dateVal);
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Simple Pie Chart Component
const PieChart = ({ data }) => {
  const total = data.safe + data.warning + data.dangerous;
  if (total === 0) return <div className="flex justify-center items-center h-32 text-gray-500">No permission data</div>;
  
  // Calculate percentages
  const safePercent = Math.round((data.safe / total) * 100);
  const warningPercent = Math.round((data.warning / total) * 100);
  const dangerousPercent = Math.round((data.dangerous / total) * 100);
  
  // Calculate offsets for pie chart
  const dangerousOffset = safePercent + warningPercent;
  
  return (
    <div className="flex flex-col items-center gap-6">
      {/* Pie chart + legend row */}
      <div className="flex items-center gap-8">
        <div className="relative w-36 h-36 shrink-0">
        <svg viewBox="0 0 36 36" className="h-full w-full">
          <circle cx="18" cy="18" r="15.91549430918954" fill="#1E1E1E" stroke="#3B3B3B" strokeWidth="0.5"></circle>
          
          {safePercent > 0 && (
            <circle 
                cx="18" cy="18" r="15.91549430918954" 
                fill="transparent" stroke="currentColor"
              className="text-status-safe"
                strokeWidth="3.5" 
              strokeDasharray={`${safePercent} ${100-safePercent}`}
              strokeDashoffset="25"
            ></circle>
          )}
          
          {warningPercent > 0 && (
            <circle 
                cx="18" cy="18" r="15.91549430918954" 
                fill="transparent" stroke="currentColor"
              className="text-status-warning"
                strokeWidth="3.5" 
              strokeDasharray={`${warningPercent} ${100-warningPercent}`}
                strokeDashoffset={100 - safePercent + 25}
            ></circle>
          )}
          
          {dangerousPercent > 0 && (
            <circle 
                cx="18" cy="18" r="15.91549430918954" 
                fill="transparent" stroke="currentColor"
              className="text-status-danger"
                strokeWidth="3.5" 
              strokeDasharray={`${dangerousPercent} ${100-dangerousPercent}`}
              strokeDashoffset={100 - dangerousOffset + 25}
            ></circle>
          )}
          
          <text x="18" y="17" textAnchor="middle" fontSize="5" fill="#FFFFFF" fontWeight="bold">
            {total}
          </text>
          <text x="18" y="22" textAnchor="middle" fontSize="2.5" fill="#CCCCCC">
            Permissions
          </text>
        </svg>
      </div>
      
        {/* Legend */}
      <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-safe shrink-0"></div>
            <span className="text-sm text-gray-300">{data.safe} Safe</span>
            <span className="text-gray-500 text-xs">({safePercent}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-warning shrink-0"></div>
            <span className="text-sm text-gray-300">{data.warning} Warning</span>
            <span className="text-gray-500 text-xs">({warningPercent}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-danger shrink-0"></div>
            <span className="text-sm text-gray-300">{data.dangerous} Dangerous</span>
            <span className="text-gray-500 text-xs">({dangerousPercent}%)</span>
          </div>
          </div>
        </div>
        
      {/* Dangerous permissions list - below chart, full width */}
        {data.dangerousList.length > 0 && (
        <div className="w-full mt-2 p-3 bg-red-950/20 border border-red-900/30 rounded-lg">
          <h4 className="text-xs font-medium text-gray-400 mb-2">Top Dangerous Permissions:</h4>
          <div className="flex flex-wrap gap-2">
              {data.dangerousList.map((perm, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 text-xs text-red-400 bg-red-950/40 px-2 py-1 rounded-md">
                <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                {perm}
              </span>
              ))}
          </div>
          </div>
        )}
    </div>
  );
};

// Main App Details View Component
const AppDetailsView = ({ 
  appDetails,
  analysisResults, 
  isLoading,
  error,
  permissionsData,
  uploadNewVersion,
  moveToNextStage 
}) => {
  // Dashboard menu items
  const menuItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard',
      icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    },
    { 
      path: '/new-project', 
      label: 'New Project',
      icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    },
    { 
      path: '/support', 
      label: 'Support',
      icon: <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    }
  ];
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <div className="bg-[#1E1E1E] p-8 rounded-xl max-w-md border border-red-800">
          <div className="flex items-center gap-3 mb-4 text-red-500">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold">Error</h2>
          </div>
          <p className="text-gray-300 mb-4">{error}</p>
          <Link to="/dashboard" className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  
  if (!appDetails) {
    return (
      <div className="flex justify-center items-center h-screen bg-black">
        <div className="bg-[#1E1E1E] p-8 rounded-xl max-w-md border border-gray-800">
          <div className="flex items-center gap-3 mb-4 text-gray-400">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold">App Not Found</h2>
          </div>
          <p className="text-gray-300 mb-4">The requested application could not be found.</p>
          <Link to="/dashboard" className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  
  // Calculate progress percentage based on status
  const currentStatus = appDetails.statusApp || AppStatus.AWAITING_UPLOAD;
  const progressPercent = AppStatusInfo[currentStatus]?.progressPercent || 0;
  
  // Determine button text based on status
  const getButtonLabel = () => {
    switch (currentStatus) {
      case AppStatus.APP_LIVE:
        return "App Is Live";
      case AppStatus.AWAITING_UPLOAD,AppStatus.ANALYZING_APK:
        return "Start Deployment";
      default:
        // For any deployment status
        return "Continue Deployment";
    }
  };
  
  // Get status display name
  const statusDisplayName = AppStatusInfo[currentStatus]?.displayName || 'Unknown Status';
  
  // Get color class based on status
  const statusColorClass = `text-${AppStatusInfo[currentStatus]?.color || 'gray'}-500`;
  
  return (
    <div className="flex w-full min-h-screen bg-black">
      {/* Fixed Sidebar */}
      <InternalMenu 
        items={menuItems} 
        variant="dashboard" 
        showUserProfile={true} 
      />
      
      {/* Main Content */}
      <main className="flex-1 md:ml-64 w-full text-white overflow-y-auto py-8 px-4 md:px-8">
        {/* Back to Dashboard Link */}
        <div className="mb-6">
          <Link to="/dashboard" className="text-gray-400 hover:text-blue-400 flex items-center gap-2 text-sm">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
        </div>
        
        {/* Top Header Section (30% of screen) */}
        <div className="bg-[#1E1E1E] rounded-xl p-6 mb-6 shadow-md">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex items-start">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-gray-800 rounded-xl flex items-center justify-center shrink-0 mr-3 md:mr-4">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-left max-w-[calc(100%-60px)] md:max-w-none">
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white truncate">{appDetails.fileName}</h1>
                
                {analysisResults?.report_summary?.package_name ? (
                  <p className="text-gray-400 text-xs md:text-sm mt-1 mb-2 truncate">
                    {analysisResults.report_summary.package_name || 'Package name not available'}
                  </p>
                ) : (
                  <div className="h-3 w-36 bg-gray-700 rounded animate-pulse my-2"></div>
                )}
                
                <div className="flex items-center flex-wrap gap-y-2">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColorClass} bg-${AppStatusInfo[currentStatus]?.color || 'gray'}-900`}>
                    {statusDisplayName}
                  </span>
                  <span className="text-gray-400 text-xs ml-3">
                    Uploaded: {formatDate(appDetails.uploadedAt)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Replace Upload button with Start Deployment Link */}
            <Link
              to={`/app-status/${appDetails.fileId}`}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-500 text-white rounded-lg transition-all hover:opacity-90 flex items-center justify-center gap-2 text-sm font-medium whitespace-nowrap mt-2 md:mt-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {getButtonLabel()}
            </Link>
          </div>
          
          {/* Progress Bar - Updated to match dashboard style */}
          <div className="mb-2">
            <div className="flex items-center space-x-2 mb-1">
              {/* Status text with appropriate color */}
              <span className={`text-sm font-medium text-${AppStatusInfo[currentStatus]?.color || 'gray'}-400`}>
                {statusDisplayName}
              </span>
              
              {/* Percentage complete */}
              <span className="text-sm text-gray-400">
                {progressPercent}%
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-700 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full bg-${AppStatusInfo[currentStatus]?.color || 'gray'}-500`}
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
          
          {/* Store URL - Display if available */}
          {appDetails.storeUrl && (
            <div className="mt-4 p-3 rounded-lg bg-gray-800 border border-gray-700 text-left">
              <div className="flex items-start">
                <div className="shrink-0 p-1.5 rounded-full bg-green-600/20 text-green-400 mr-3">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium mb-1">
                    Google Play Store URL
                  </p>
                  <a 
                    href={appDetails.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 text-sm break-all transition-colors"
                  >
                    {appDetails.storeUrl}
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {/* Positive message about deployment - Moved from security findings section to top section */}
          {analysisResults?.report_summary?.findings && (
            <div className="mt-4 p-3 rounded-lg bg-blue-600/10 border border-blue-700/40 text-left">
              <div className="flex items-start">
                <div className="shrink-0 p-1.5 rounded-full bg-blue-600/20 text-blue-400 mr-3">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-blue-300 text-sm">
                    Despite some areas for improvement, this application can proceed to the next stages of deployment. You may continue with the publishing process while addressing these recommendations.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Middle Section - App Details (40% of screen) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* App Information Panel */}
          <div className="bg-[#1E1E1E] rounded-xl p-4 md:p-6 shadow-md">
            <h2 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6">Application Details</h2>
            <div className="space-y-4 md:space-y-5">
              {[ // Define details as an array for cleaner mapping
                { label: "Version Name", value: analysisResults?.report_summary?.version_name },
                { label: "Version Code", value: analysisResults?.report_summary?.version_code },
                { label: "Min SDK Version", value: analysisResults?.report_summary?.min_sdk_version },
                { label: "Target SDK Version", value: analysisResults?.report_summary?.target_sdk_version },
                { label: "File Size", value: appDetails.fileSize || analysisResults?.report_summary?.file_size },
                { label: "First Upload", value: appDetails.uploadedAt ? formatDate(appDetails.uploadedAt) : null },
                { label: "Last Updated", value: (appDetails.lastUpdated || appDetails.lastStatusUpdate) ? formatDate(appDetails.lastUpdated || appDetails.lastStatusUpdate) : null },
                { label: "Store URL", value: appDetails.storeUrl || null, isLink: true },
              ].map((detail, index) => (
                <div key={index} className="flex justify-between items-center border-b border-gray-800 pb-3">
                  <p className="text-gray-400 text-sm text-left">{detail.label}</p>
                  {analysisResults || (detail.label === "File Size" || detail.label.includes("Upload") || detail.label === "Store URL") ? (
                    detail.isLink && detail.value ? (
                      <a 
                        href={detail.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm text-right transition-colors truncate max-w-[200px]"
                      >
                        {detail.value}
                      </a>
                    ) : (
                      <p className="text-white font-medium text-right truncate">{detail.value || 'N/A'}</p>
                    )
                  ) : (
                    <div className="flex items-center justify-end">
                      <div className="h-3 bg-gray-700 rounded animate-pulse w-20"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Status History Panel */}
          {!appDetails.statusHistory || appDetails.statusHistory.length === 0 ? (
            <div className="bg-[#1E1E1E] rounded-xl p-4 md:p-6 shadow-md flex flex-col items-center justify-center min-h-[300px]">
              <div className="animate-pulse flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-gray-300 text-lg font-medium mb-2">Status History</h3>
                <p className="text-gray-500 text-center">Data will be displayed when analysis completes successfully.</p>
              </div>
            </div>
          ) : (
            <div className="bg-[#1E1E1E] rounded-xl p-6 shadow-md">
              <h2 className="text-xl font-bold text-white mb-4">Status History</h2>
              <div className="space-y-4">
                {appDetails.statusHistory.map((history, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className={`w-2 h-2 mt-2 rounded-full bg-${AppStatusInfo[history.status]?.color || 'gray'}-500 shrink-0`}></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{AppStatusInfo[history.status]?.displayName || history.status}</p>
                        <span className="text-gray-500 text-xs">
                          {formatDate(history.timestamp)}
                        </span>
                      </div>
                      {history.notes && (
                        <p className="text-gray-400 text-sm mt-1">{history.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Version History Section */}
        {appDetails.versions && appDetails.versions.length > 1 && (
          <div className="bg-[#1E1E1E] rounded-xl p-6 md:p-8 shadow-md mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Version History</h2>
              <span className="text-sm text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
                {appDetails.versions.length} versions
              </span>
            </div>
            <div className="space-y-3">
              {[...appDetails.versions]
                .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))
                .map((version, index) => {
                  const isLatest = index === 0;
                  const uploadDate = version.uploadedAt 
                    ? (typeof version.uploadedAt === 'string' 
                        ? new Date(version.uploadedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : typeof version.uploadedAt?.toDate === 'function'
                          ? version.uploadedAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : formatDate(version.uploadedAt))
                    : 'N/A';
                  
                  return (
                    <div 
                      key={version.fileId || index} 
                      className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                        isLatest 
                          ? 'bg-purple-950/20 border-purple-800/40' 
                          : 'bg-gray-800/40 border-gray-700/40'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Version number badge */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          isLatest ? 'bg-purple-600' : 'bg-gray-700'
                        }`}>
                          <span className="text-white text-sm font-bold">v{version.versionNumber || '?'}</span>
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium text-sm">Version {version.versionNumber}</p>
                            {isLatest && (
                              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                                Latest
                              </span>
                            )}
                            {version.statusApp && (
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full bg-${AppStatusInfo[version.statusApp]?.color || 'gray'}-900 text-${AppStatusInfo[version.statusApp]?.color || 'gray'}-400`}>
                                {AppStatusInfo[version.statusApp]?.displayName || version.statusApp}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-400">{uploadDate}</span>
                            {version.fileSize && (
                              <span className="text-xs text-gray-500">• {version.fileSize}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* View details link for this version */}
                      <Link 
                        to={`/app-details/${version.fileId}`}
                        className="px-3 py-1.5 border border-gray-600 text-xs font-medium text-gray-300 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
                      >
                        {isLatest ? 'Current' : 'View'}
                      </Link>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        
        {/* Analysis Results - Permissions Section */}
        {!analysisResults || !analysisResults.report_summary ? (
          <div className="bg-[#1E1E1E] rounded-xl p-8 shadow-md mb-6 flex flex-col items-center justify-center min-h-[350px]">
            <div className="animate-pulse flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-gray-300 text-xl font-medium mb-3">Analysis Results Summary</h3>
              <p className="text-gray-500 text-center text-sm">Data will be displayed when analysis completes successfully.</p>
            </div>
          </div>
        ) : (
          <div className="bg-[#1E1E1E] rounded-xl p-6 md:p-8 shadow-md mb-6">
            {/* Header with success badge */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-white">Analysis Results Summary</h2>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-950/40 border border-emerald-800/30 rounded-full">
                <svg className="w-4 h-4 text-status-safe shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-emerald-400 text-xs font-medium">Completed</span>
              </div>
                  </div>
                  
            {/* Stats Grid - Full width row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
              <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-white mb-1">{analysisResults?.report_summary?.exported_activities || 0}</p>
                <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Activities</p>
                    </div>
              <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-white mb-1">{analysisResults?.report_summary?.exported_services || 0}</p>
                <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Services</p>
                    </div>
              <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-white mb-1">{analysisResults?.report_summary?.libraryCount || 0}</p>
                <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Libraries</p>
                    </div>
              <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-4 text-center">
                <p className={`text-3xl font-bold mb-1 ${
                        analysisResults?.report_summary?.security_score >= 70 ? 'text-status-safe' :
                        analysisResults?.report_summary?.security_score >= 40 ? 'text-status-warning' :
                        'text-status-danger'
                      }`}>
                        {analysisResults?.report_summary?.security_score || 'N/A'}
                      </p>
                <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Security Score</p>
                    </div>
                  </div>
            
            {/* Permission Analysis - Centered */}
            <div>
              <h3 className="text-lg font-medium text-gray-300 mb-6">Permission Analysis</h3>
              <div className="flex justify-center">
                <PieChart data={permissionsData} />
              </div>
            </div>
          </div>
        )}
        
        {/* Security Findings Section */}
        {analysisResults?.report_summary?.findings && (
          <div className="bg-[#1E1E1E] rounded-xl p-6 md:p-8 shadow-md mb-6">
            <h2 className="text-xl font-bold text-white mb-6">Security Findings</h2>
            
            {/* Summary counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className={`rounded-xl p-4 text-center border ${
                (analysisResults.report_summary.findings.high || 0) > 0 
                  ? 'bg-red-950/30 border-red-800/40' 
                  : 'bg-gray-800/60 border-gray-700/50'
              }`}>
                <div className={`text-3xl font-bold mb-1 ${
                  (analysisResults.report_summary.findings.high || 0) > 0 ? 'text-red-400' : 'text-gray-400'
                }`}>{analysisResults.report_summary.findings.high || 0}</div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Critical</div>
              </div>
              <div className={`rounded-xl p-4 text-center border ${
                (analysisResults.report_summary.findings.warning || 0) > 0 
                  ? 'bg-amber-950/30 border-amber-800/40' 
                  : 'bg-gray-800/60 border-gray-700/50'
              }`}>
                <div className={`text-3xl font-bold mb-1 ${
                  (analysisResults.report_summary.findings.warning || 0) > 0 ? 'text-amber-400' : 'text-gray-400'
                }`}>{analysisResults.report_summary.findings.warning || 0}</div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Warnings</div>
              </div>
              <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-1">{analysisResults.report_summary.findings.info || 0}</div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Info</div>
              </div>
              <div className="bg-emerald-950/30 border border-emerald-800/40 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-emerald-400 mb-1">{analysisResults.report_summary.findings.secure || 0}</div>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Secure</div>
              </div>
            </div>
            
            {/* Detailed findings list */}
            {analysisResults.report_summary.security_findings && 
             analysisResults.report_summary.security_findings.length > 0 && (
              <div className="space-y-3 mb-6">
                {/* Critical & Warning findings first */}
                {analysisResults.report_summary.security_findings
                  .filter(f => f.severity === 'critical' || f.severity === 'warning')
                  .map((finding, idx) => (
                    <div key={finding.id || idx} className={`p-4 rounded-xl border text-left ${
                      finding.severity === 'critical' 
                        ? 'bg-red-950/20 border-red-900/30' 
                        : 'bg-amber-950/20 border-amber-900/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className={`shrink-0 mt-0.5 p-1.5 rounded-full ${
                          finding.severity === 'critical' 
                            ? 'bg-red-500/10 text-red-400' 
                            : 'bg-amber-500/10 text-amber-400'
                        }`}>
                          {finding.severity === 'critical' ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="text-sm font-semibold text-white">{finding.title}</h4>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                              finding.severity === 'critical' 
                                ? 'bg-red-500/20 text-red-400' 
                                : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {finding.severity}
                            </span>
                            {finding.category && (
                              <span className="text-[10px] font-medium uppercase px-2 py-0.5 rounded-full bg-gray-700/50 text-gray-400">
                                {finding.category}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-xs leading-relaxed mb-2">{finding.description}</p>
                          <div className="flex items-start gap-1.5">
                            <svg className="w-3 h-3 text-blue-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-blue-400/80 text-xs">{finding.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                }

                {/* Info findings */}
                {analysisResults.report_summary.security_findings
                  .filter(f => f.severity === 'info')
                  .map((finding, idx) => (
                    <div key={finding.id || idx} className="p-4 rounded-xl border bg-blue-950/10 border-blue-900/20 text-left">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 mt-0.5 p-1.5 rounded-full bg-blue-500/10 text-blue-400">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="text-sm font-semibold text-white">{finding.title}</h4>
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">info</span>
                            {finding.category && (
                              <span className="text-[10px] font-medium uppercase px-2 py-0.5 rounded-full bg-gray-700/50 text-gray-400">
                                {finding.category}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-xs leading-relaxed">{finding.description}</p>
                        </div>
                      </div>
                    </div>
                  ))
                }

                {/* Secure findings - collapsed by default */}
                {analysisResults.report_summary.security_findings
                  .filter(f => f.severity === 'secure').length > 0 && (
                  <div className="p-4 rounded-xl border bg-emerald-950/10 border-emerald-900/20 text-left">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-0.5 p-1.5 rounded-full bg-emerald-500/10 text-emerald-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-emerald-300 mb-2">
                          {analysisResults.report_summary.security_findings.filter(f => f.severity === 'secure').length} checks passed
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {analysisResults.report_summary.security_findings
                            .filter(f => f.severity === 'secure')
                            .map((finding, idx) => (
                              <span key={finding.id || idx} className="inline-flex items-center gap-1 text-xs text-emerald-400/80 bg-emerald-950/30 px-2.5 py-1 rounded-md">
                                <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {finding.title}
                              </span>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Overall security recommendation */}
            <div className={`p-4 rounded-xl border text-left ${
              (analysisResults.report_summary.findings.high > 0) 
                ? 'bg-red-950/20 border-red-900/30' 
                : 'bg-emerald-950/20 border-emerald-900/30'
            }`}>
              <div className="flex items-start">
                <div className={`shrink-0 p-2 rounded-full mr-3 ${
                  (analysisResults.report_summary.findings.high > 0) ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'
                }`}>
                  {(analysisResults.report_summary.findings.high > 0) ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  )}
                </div>
                <div className="text-left">
                  <h3 className="text-base font-medium text-white mb-1">
                    {(analysisResults.report_summary.findings.high > 0) ? 'Security Issues Detected' : 'No Critical Security Issues'}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {(analysisResults.report_summary.findings.high > 0) 
                      ? `This application has ${analysisResults.report_summary.findings.high} critical security issue${analysisResults.report_summary.findings.high > 1 ? 's' : ''} that should be addressed before release.`
                      : 'This application passed the security review. No critical security issues were detected during the analysis.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AppDetailsView; 